import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export function useDashboardActivityRealtime(enabled: boolean = true) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const initialLoadRef = useRef(true);
  const invalidationTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!enabled || !user) return;

    // Mark initial load complete after delay
    const initialLoadTimeout = setTimeout(() => {
      initialLoadRef.current = false;
    }, 2000);

    // Debounced invalidation function
    const invalidateActivities = () => {
      if (invalidationTimeoutRef.current) {
        clearTimeout(invalidationTimeoutRef.current);
      }
      
      invalidationTimeoutRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ 
          queryKey: ['dashboard-activities', user.id] 
        });
      }, 500); // 500ms debounce
    };

    // Create single channel for all subscriptions
    const channel = supabase.channel('dashboard-activity-changes');

    // Subscribe to all activity-related tables
    const tables = [
      'activities',
      'tasks', 
      'leads',
      'projects',
      'contacts',
      'account_plans',
      'opportunities',
      'opportunity_stage_history'
    ];

    tables.forEach(table => {
      // Listen for INSERTs
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: table,
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (initialLoadRef.current) return;
          console.log(`New ${table}:`, payload.new);
          invalidateActivities();
        }
      );

      // Listen for UPDATEs (for task completion, status changes)
      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: table,
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (initialLoadRef.current) return;
          console.log(`Updated ${table}:`, payload.new);
          invalidateActivities();
        }
      );
    });

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Dashboard activity realtime subscriptions active');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('Dashboard activity subscription error');
      }
    });

    return () => {
      clearTimeout(initialLoadTimeout);
      if (invalidationTimeoutRef.current) {
        clearTimeout(invalidationTimeoutRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [enabled, user, queryClient]);
}
